import React, { useState } from 'react';
import { servicesService } from './servicesService';
import { useAuth } from '../../hooks/useAuth';
import { Office } from '../../types';
import { X, Loader, Upload, FileText } from 'lucide-react';

interface ImportServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ParsedRow {
  name: string;
  url: string;
  username: string;
  password: string;
  description: string;
  notes: string;
}

export const ImportServicesModal: React.FC<ImportServicesModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [office, setOffice] = useState<Office>('shared');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [successCount, setSuccessCount] = useState<number | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setParsedRows([]);
      setError('');
      setSuccessCount(null);
    }
  };

  const handleParse = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    setError('');
    setParsedRows([]);
    setSuccessCount(null);

    try {
      const text = await file.text();
      const lines = text.split('\n');

      if (lines.length < 2) {
        setError('CSV file appears to be empty or has no data rows');
        return;
      }

      // Detect delimiter from first line
      const firstLine = lines[0];
      const delimiter = firstLine.includes(';') ? ';' : ',';

      // Skip header row (index 0), parse remaining rows
      const rows: ParsedRow[] = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const columns = line.split(delimiter).map(col => col.trim().replace(/^"|"$/g, ''));

        const name = columns[0] || '';
        if (!name) continue; // Skip empty rows

        rows.push({
          name,
          url: columns[1] || '',
          username: columns[2] || '',
          password: columns[3] || '',
          description: columns[4] || '',
          notes: columns[5] || '',
        });
      }

      if (rows.length === 0) {
        setError('No valid data rows found in the CSV file');
        return;
      }

      setParsedRows(rows);
    } catch (err: any) {
      setError(err.message || 'Failed to parse CSV file');
    }
  };

  const handleImport = async () => {
    if (!user || parsedRows.length === 0) return;

    setImporting(true);
    setError('');

    try {
      const services = parsedRows.map(row => ({
        name: row.name,
        url: row.url,
        username: row.username,
        password: row.password,
        description: row.description,
        category: '',
        office,
        cost: null as number | null,
        billingCycle: 'monthly' as const,
        currency: 'NOK' as const,
        renewalDate: null as Date | null,
        tags: [] as string[],
        notes: row.notes,
        claimed: false,
        deleted: false,
        createdBy: user.email!,
      }));

      const count = await servicesService.importServices(services);
      setSuccessCount(count);
      setParsedRows([]);
      setFile(null);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to import services');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (importing) return;
    setFile(null);
    setParsedRows([]);
    setError('');
    setSuccessCount(null);
    setOffice('shared');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Import Services from CSV</h2>
            <button
              onClick={handleClose}
              disabled={importing}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={24} />
            </button>
          </div>

          {/* Success message */}
          {successCount !== null && (
            <div className="bg-green-100 text-green-700 p-3 rounded mb-4">
              Successfully imported {successCount} services!
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* File input and office selector */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">CSV File</label>
                <div className="flex items-center gap-2">
                  <label className="flex-1 flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <Upload size={20} className="text-gray-400" />
                    <span className="text-sm text-gray-600 truncate">
                      {file ? file.name : 'Choose a CSV file...'}
                    </span>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Expected columns: Tjeneste, Nettadresse, Brukernavn, Passord, Beskrivelse, Merknad
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Office</label>
                <select
                  value={office}
                  onChange={(e) => setOffice(e.target.value as any)}
                  className="w-full p-3 border rounded-lg"
                >
                  <option value="stavanger">Stavanger</option>
                  <option value="tønsberg">Tønsberg</option>
                  <option value="oslo">Oslo</option>
                  <option value="shared">Shared</option>
                </select>
              </div>
            </div>

            {/* Parse button */}
            {file && parsedRows.length === 0 && (
              <button
                type="button"
                onClick={handleParse}
                className="bg-secondary text-secondary-foreground px-4 py-2 rounded-lg hover:bg-secondary/80 transition-colors border flex items-center gap-2"
              >
                <FileText size={16} />
                Parse CSV
              </button>
            )}

            {/* Preview table */}
            {parsedRows.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-2">
                  Preview ({parsedRows.length} rows)
                </h3>
                <div className="border rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 border-b font-medium">#</th>
                        <th className="text-left p-2 border-b font-medium">Name</th>
                        <th className="text-left p-2 border-b font-medium">URL</th>
                        <th className="text-left p-2 border-b font-medium">Username</th>
                        <th className="text-left p-2 border-b font-medium">Password</th>
                        <th className="text-left p-2 border-b font-medium">Description</th>
                        <th className="text-left p-2 border-b font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row, index) => (
                        <tr key={index} className="border-b last:border-b-0 hover:bg-gray-50">
                          <td className="p-2 text-muted-foreground">{index + 1}</td>
                          <td className="p-2 font-medium">{row.name}</td>
                          <td className="p-2 truncate max-w-[150px]">{row.url}</td>
                          <td className="p-2">{row.username}</td>
                          <td className="p-2">{row.password ? '••••••' : ''}</td>
                          <td className="p-2 truncate max-w-[150px]">{row.description}</td>
                          <td className="p-2 truncate max-w-[150px]">{row.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Import button */}
            {parsedRows.length > 0 && (
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={importing}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-accent disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={importing}
                  className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg border border-primary flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
                >
                  {importing ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Import {parsedRows.length} Services
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Close button when no data */}
            {parsedRows.length === 0 && successCount === null && (
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-accent"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Close button after success */}
            {successCount !== null && (
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-accent"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
